import type React from 'react';
import { MDText } from 'i18n-react';

import vehicleHistoryChecksText, {
	VEHICLE_HISTORY_CHECK_TEXT_KEYS,
} from 'Components/VehicleHistoryChecks/VehicleHistoryChecks.texts';
import type { Vehicle } from 'Types/vehicle';
import { formatDate } from 'Utilities/formatters';

import { VEHICLE_HISTORY_CHECKS_DISPLAY_ORDER } from './useVehicleHistoryChecks.consts';
import type { VehicleHistoryCheckList } from './useVehicleHistoryChecks.types';

const LocalT = new MDText(vehicleHistoryChecksText);

export const getAlertCount = (alerts: VehicleHistoryCheckList[] = []): number =>
	alerts.filter(({ isWarning }) => isWarning).length;

export const getVehicleHistoryCheckText = ({
	isWarning = false,
	key,
	translateArgs = {},
}: Omit<VehicleHistoryCheckList, 'text'>): React.ReactNode =>
	LocalT.translate(`${key}.${isWarning ? 'warning' : 'success'}`, translateArgs);

const getVehicleHistoryChecksConfig = (historyChecks: Vehicle['historyChecks']) => {
	const {
		financeAgreementsCount,
		hasBeenImported,
		hasBeenSalvaged,
		hasBeenStolen,
		hasBeenWrittenOffByDamage,
		hasBeenWrittenOffByTheft,
		hasSecurityWatch,
		isPlateTransferred,
		motExpiryDate,
		taxiUse,
	} = historyChecks || {};

	return {
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.financeAgreement]: {
			isWarning: Boolean(financeAgreementsCount),
			translateArgs: {
				agreement: `agreement${financeAgreementsCount === 1 ? '' : 's'}`,
				agreementCount: financeAgreementsCount,
			},
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.importCheck]: {
			isWarning: hasBeenImported,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffDamage]: {
			isWarning: hasBeenWrittenOffByDamage,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.insuranceWriteOffInsurance]: {
			isWarning: hasBeenWrittenOffByTheft,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.mot]: {
			isWarning: Boolean(motExpiryDate),
			translateArgs: {
				expiryDate: formatDate(motExpiryDate),
			},
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.plateTransfer]: {
			isWarning: isPlateTransferred,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.salvageCheck]: {
			isWarning: hasBeenSalvaged,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.securityCheck]: {
			isWarning: hasSecurityWatch,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.stolen]: {
			isWarning: hasBeenStolen,
		},
		[VEHICLE_HISTORY_CHECK_TEXT_KEYS.taxiCheck]: {
			isWarning: Boolean(taxiUse?.dateFrom && taxiUse?.dateTo),
			translateArgs: {
				dateFrom: formatDate(taxiUse?.dateFrom),
				dateTo: formatDate(taxiUse?.dateTo),
				source: taxiUse?.source,
			},
		},
	};
};

const sortWarningsToTopOfList = (list: VehicleHistoryCheckList[]): VehicleHistoryCheckList[] =>
	[...list].sort((a, b) => (a?.isWarning && !b?.isWarning ? -1 : 1));

export const getVehicleHistoryChecksList = (historyChecks: Vehicle['historyChecks']): VehicleHistoryCheckList[] => {
	const config = getVehicleHistoryChecksConfig(historyChecks);

	const list: VehicleHistoryCheckList[] = VEHICLE_HISTORY_CHECKS_DISPLAY_ORDER.map((key) => {
		const item = config?.[key];
		const { isWarning } = item || {};
		return { isWarning, key, text: getVehicleHistoryCheckText({ ...item, key }) };
	});

	return sortWarningsToTopOfList(list);
};
